import { TypedDocumentNode, gql, useSuspenseQuery } from '@apollo/client';
import classNames from 'classnames';
import { Suspense, useMemo, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTimeoutFn } from 'react-use';
import styled, { keyframes } from 'styled-components';

import { useTypedParams } from '@sorare/routing';

import {
  Position,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { AnimatedNumber } from '@sorare/core/src/atoms/animations/AnimatedNumber';
import { EmojiFlood } from '@sorare/core/src/atoms/animations/EmojiFlood';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { MinigamePopup } from '@sorare/core/src/components/MinigamePopup';
import { Stadium } from '@sorare/core/src/components/animations/Stadium';
import { CardImg } from '@sorare/core/src/components/card/CardImg';
import { PositionIcon } from '@sorare/core/src/components/lineup/PositionIcon';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { NBA } from '@sorare/core/src/constants/__generated__/routes';
import { useConnectionContext } from '@sorare/core/src/contexts/connection';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { isType } from '@sorare/core/src/gql';
import { useTransitionApi } from '@sorare/core/src/hooks/ui/useTransitionApi';
import { range } from '@sorare/core/src/lib/arrays';
import { randomIntFromInterval } from '@sorare/core/src/lib/math';
import { positionNames } from '@sorare/core/src/lib/nba';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thinMaterial } from '@sorare/core/src/style/utils';

import popupBackground from 'assets/minigames/popupBackground.jpg';
import field from 'pages/minigames/assets/stadium.jpg';
import {
  EASTERN_CONFERENCE,
  WESTERN_CONFERENCE,
} from 'pages/minigames/pve_/PLAYERS';

import {
  PVENBAQuery,
  PVENBAQueryVariables,
} from './__generated__/page.graphql';
import { PageParams } from './__generated__/routeParams';

type NBACard = PVENBAQuery['anyCards'][number] & { __typename: 'NBACard' };

type GameResult = 'Win' | 'Lose' | 'Tie';

const Root = styled(Vertical)`
  position: relative;
  z-index: 1;
  justify-content: space-between;
  height: var(--100vh);
  overflow: hidden;
`;

const StadiumWrapper = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  bottom: -25vmax;
  pointer-events: none;
`;

const appearIn = keyframes`
  0% {
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  50% {
    scale: 1.1;
  }
  100% {
    scale: 1;
  }
`;
const Slots = styled(Vertical)`
  border-radius: var(--unit);
  background: var(--c-nd-100);
  ${thinMaterial}
  gap: var(--half-unit);
  padding: 1.5vmin;
`;
const Slot = styled(Vertical)`
  flex: 1;
  gap: 1;
`;

const CardImgWrapper = styled.div`
  width: 100%;
  aspect-ratio: var(--card-aspect-ratio);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-100);
  overflow: hidden;
  > *:first-child {
    animation: 0.3s ease ${appearIn} forwards;
  }
`;
const pulse = keyframes`
    0% {
        transform: scale(0.98);
    }

    50% {
        transform: scale(1.02);
    }

    100% {
        transform: scale(0.98);
    }
`;

const pulsate = keyframes`
    0% {
        transform: scale(1);
        opacity: 1;
    }

    50% {
        transform: scale(1.2);
        opacity: 0;
    }

    100% {
        transform: scale(1);
        opacity: 0;
    }
`;

const Pulsation = styled.span`
  inset: 0;
  border: 2px solid white;
  border-radius: var(--unit);
  position: absolute;
  animation: ${pulsate} 2s infinite;
  aspect-ratio: var(--card-aspect-ratio);
  opacity: 0;
`;

const Pick = styled.div`
  animation: 0.3s ease ${appearIn} forwards;
  animation: ${pulse} 1s 4s infinite;
`;

const CenterContent = styled(Horizontal)`
  padding: 0 var(--double-unit);
  margin: 5% 0;
  width: 100%;
  column-gap: 25%;
  justify-content: space-around;
`;

const Content = styled(Vertical)`
  width: 500px;
  max-width: 100%;
  margin: auto;
  justify-content: space-around;
  flex: 1;
`;

const Heading = styled(Vertical)`
  bottom: 100%;
  text-align: center;
  width: 100%;
`;

const StyledHeading = styled.h2`
  font-size: 3.2vmin;
`;

const StyledBody = styled.p`
  font-size: 1.8vmin;
  color: var(--c-nd-600);
`;

const ResultRoot = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  flex: 1;
  z-index: 1;
  justify-content: space-around;
  transform-origin: center;
  opacity: 0;
  pointer-events: none;
  &.showResult {
    pointer-events: auto;
    animation: 0.3s ease ${fadeIn} forwards;
  }
`;

const ScoreTitle = styled(Horizontal).attrs({ gap: 0, center: true })`
  font-size: 60px;
  line-height: 1;
  text-transform: uppercase;
  font-family: var(--rigid-square);
  z-index: 1;
  @media ${tabletAndAbove} {
    font-size: 80px;
  }
`;

const SlotPlaceholder = styled(Vertical)`
  height: 100%;
  opacity: 0.1;
  word-break: break-word;
  padding: var(--half-unit);
  &.selected {
    opacity: 1;
    background: rgba(var(--c-rgb-white), 0.05);
  }
`;

const PickButton = styled(ButtonBase)`
  position: relative;
  max-width: 30vw;
  flex: 1;
`;

const messages = defineMessages({
  yourTeam: {
    id: 'nba.minigames.pve_.yourTeam',
    defaultMessage: 'Your Team',
  },
  opponentTeam: {
    id: 'nba.minigames.pve_.opponentTeam',
    defaultMessage: 'Your opponent',
  },
});

const randomIntExcept = (min: number, max: number, excepts: number[]) => {
  let result;
  do {
    result = Math.floor(Math.random() * (max - min + 1) + min);
  } while (excepts.includes(result));
  return result;
};

const DelayedScoring = ({
  score: scoreProp,
  index,
}: {
  score: number;
  index: number;
}) => {
  const [score, setScore] = useState(0);
  useTimeoutFn(() => setScore(scoreProp), index * 600);
  return (
    <div style={{ opacity: score, transition: '.3s ease opacity' }}>
      <Score
        score={score}
        animationDelayMs={0}
        scoreStatus={ScoreStatus.FINAL}
        scoreType="PLAYER_SCORE"
      />
    </div>
  );
};
const Result = ({
  yourTeam,
  opponentTeam,
  showResult,
}: {
  yourTeam: NBACard[];
  opponentTeam: NBACard[];
  showResult: boolean;
}) => {
  const [displayResult, setDisplayResult] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useConnectionContext();
  const { currentUser } = useCurrentUserContext();

  useTimeoutFn(() => {
    setDisplayResult(true);
  }, 3000);
  const yourScore = yourTeam.reduce((acc, cur) => (cur.score || 0) + acc, 0);
  const opponentScore = opponentTeam.reduce(
    (acc, cur) => (cur.score || 0) + acc,
    0
  );

  const handleNavigate = () => (currentUser ? navigate(NBA) : signUp());

  let result: GameResult;
  if (yourScore > opponentScore) {
    result = 'Win';
  } else if (yourScore === opponentScore) {
    result = 'Tie';
  } else {
    result = 'Lose';
  }

  return (
    <ResultRoot className={classNames({ showResult })}>
      <ScoreTitle>
        <AnimatedNumber
          value={opponentScore}
          animationProps={{ config: { duration: 3000 } }}
        />
      </ScoreTitle>
      <MinigamePopup
        onConfirm={handleNavigate}
        popupBackground={popupBackground}
        result={result}
        displayResult={displayResult}
      />
      <ScoreTitle>
        <AnimatedNumber
          value={yourScore}
          animationProps={{ config: { duration: 3000 } }}
        />
      </ScoreTitle>
      <div style={{ pointerEvents: 'none' }}>
        {displayResult && (
          <EmojiFlood emoji={yourScore > opponentScore ? '🔥' : '🤣'} />
        )}
      </div>
    </ResultRoot>
  );
};

const POSITION = ['Guard', 'Guard', 'Forward', 'Forward', 'Forward'] as const;

const CONFERENCES_DATA_MAP = {
  western: {
    data: WESTERN_CONFERENCE,
    title: 'Western',
  },
  eastern: {
    data: EASTERN_CONFERENCE,
    title: 'Eastern',
  },
};

const buildData = (cards: NBACard[]) => {
  const pickedPlayers: number[] = [];
  const data: NBACard[][] = [];

  const Guard = cards.filter(card =>
    card.anyPositions.includes(Position.basketball_guard)
  );
  const Forward = cards.filter(card =>
    card.anyPositions.includes(Position.basketball_forward)
  );

  const playersData = { Guard, Forward };

  if (!playersData) {
    return [];
  }
  const allPlayers = [
    ...Guard,
    ...Guard,
    ...Forward,
    ...Forward,
    ...Forward,
  ] as const;

  POSITION.forEach((pos, i) => {
    const playersLength = playersData[pos].length;
    const firstPlayerIndex = randomIntFromInterval(0, playersLength - 1);
    const secondPlayerIndex = randomIntExcept(0, playersLength - 1, [
      firstPlayerIndex,
    ]);
    const positionsBefore = POSITION.slice(0, i);
    const beginningIndex = positionsBefore.reduce(
      (acc, cur) => acc + playersData[cur].length,
      0
    );
    const finalFirstPlayerIndex = beginningIndex + firstPlayerIndex;
    const finalSecondPlayerIndex = beginningIndex + secondPlayerIndex;
    data.push([
      allPlayers[finalFirstPlayerIndex],
      allPlayers[finalSecondPlayerIndex],
    ]);
    pickedPlayers.push(finalFirstPlayerIndex, finalSecondPlayerIndex);
  });
  return data;
};

type TeamProp = {
  team: NBACard[];
  displayResult: boolean;
  label: string;
  currentSelection: number;
  viewTransition?: boolean;
};

const POSITIONS_MAP = [
  {
    position: Position.basketball_guard,
    label: 'Guard',
  },
  {
    position: Position.basketball_guard,
    label: 'Guard',
  },
  {
    position: Position.basketball_forward,
    label: 'Front',
  },
  {
    position: Position.basketball_forward,
    label: 'Front',
  },
  {
    position: Position.basketball_forward,
    label: 'Front',
  },
];

const PVE_NBA_QUERY = gql`
  query PVENBAQuery($slugs: [String!]!) {
    anyCards(slugs: $slugs) {
      slug
      ... on NBACard {
        slug
        anyPositions
        averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
      }
      pictureUrl
      score: averageScore(type: LAST_FIVE_SO5_AVERAGE_SCORE)
    }
  }
` as TypedDocumentNode<PVENBAQuery, PVENBAQueryVariables>;
const Team = ({
  team,
  displayResult,
  label,
  currentSelection,
  viewTransition = false,
}: TeamProp) => {
  return (
    <Slots>
      <LabelS color="var(--c-nd-700)" bold>
        {label}
      </LabelS>
      <Horizontal>
        {range(5).map(i => {
          const position = POSITIONS_MAP[i];
          return (
            <Slot key={i} className="bottom">
              <CardImgWrapper>
                {team.at(i) ? (
                  <CardImg
                    src={team[i].pictureUrl}
                    style={{
                      viewTransitionName: viewTransition
                        ? `card-${team[i].slug}`
                        : undefined,
                    }}
                    width={320}
                  />
                ) : (
                  <SlotPlaceholder
                    center
                    className={classNames({ selected: currentSelection === i })}
                  >
                    <PositionIcon position={POSITIONS_MAP[i].position} />
                    <LabelS bold>
                      {position.position === Position.basketball_guard ? (
                        <FormattedMessage {...positionNames.GUARD} />
                      ) : (
                        <FormattedMessage {...positionNames.FORWARD} />
                      )}
                    </LabelS>
                  </SlotPlaceholder>
                )}
              </CardImgWrapper>
              {team.at(i) ? (
                <>
                  {displayResult ? (
                    <DelayedScoring score={team[i].score || 0} index={i} />
                  ) : (
                    <Score score={0} scoreStatus={ScoreStatus.PENDING} />
                  )}
                </>
              ) : (
                <div style={{ opacity: 0 }}>
                  <Score score={0} scoreStatus={ScoreStatus.PENDING} />
                </div>
              )}
            </Slot>
          );
        })}
      </Horizontal>
    </Slots>
  );
};

const PvEPageImplementation = ({
  playersData,
  fieldUrl,
  conferenceTitle,
}: {
  playersData: NBACard[][];
  fieldUrl: string;
  conferenceTitle: string;
}) => {
  const { formatMessage } = useIntl();
  const { updateDOM } = useTransitionApi();
  const isAIPick = false;
  const [state, setState] = useState<
    Record<'yourTeam' | 'opponentTeam', NBACard[]>
  >({
    yourTeam: [],
    opponentTeam: [],
  });
  const [currentSelection, setCurrentSelection] = useState(0);
  const displayResult = state.yourTeam.length === 5;

  const selectionData = playersData[currentSelection];

  return (
    <Root>
      <Content gap={0}>
        <Team
          team={state.opponentTeam}
          displayResult={displayResult}
          label={formatMessage(messages.opponentTeam)}
          currentSelection={currentSelection}
        />
        {displayResult ? (
          <Result
            showResult={displayResult}
            yourTeam={state.yourTeam}
            opponentTeam={state.opponentTeam}
          />
        ) : (
          <>
            <Heading gap={0}>
              <StyledHeading>
                <FormattedMessage
                  id="nba.minigames.pve_.Picker.title"
                  defaultMessage="Think you know ball?"
                />
                <br />
                <FormattedMessage
                  id="nba.minigames.pve_.Picker.subtitle"
                  defaultMessage="Pick your dream {cardinal} Conference roster"
                  values={{
                    cardinal: conferenceTitle,
                  }}
                />
              </StyledHeading>
              <StyledBody>
                <FormattedMessage
                  id="nba.minigames.pve_.Picker.description"
                  defaultMessage="Scores are based on real stats from the past 5 games"
                />
              </StyledBody>
            </Heading>
            <CenterContent>
              <PickButton
                onClick={() => {
                  updateDOM(() => {
                    setState(prevState => ({
                      yourTeam: prevState.yourTeam.concat(selectionData[0]),
                      opponentTeam: prevState.opponentTeam.concat(
                        selectionData[
                          isAIPick ? randomIntFromInterval(0, 1) : 1
                        ]
                      ),
                    }));

                    setCurrentSelection(prevState => prevState + 1);
                  });
                }}
              >
                <Pick key={currentSelection}>
                  <CardImg
                    src={selectionData[0].pictureUrl}
                    style={{
                      viewTransitionName: `card-${selectionData[0].slug}`,
                    }}
                    width={320}
                  />
                </Pick>
                {currentSelection === 0 && <Pulsation />}
              </PickButton>
              <PickButton
                onClick={() => {
                  updateDOM(() => {
                    setState(prevState => ({
                      yourTeam: prevState.yourTeam.concat(selectionData[1]),
                      opponentTeam: prevState.opponentTeam.concat(
                        selectionData[
                          isAIPick ? randomIntFromInterval(0, 1) : 0
                        ]
                      ),
                    }));
                    setCurrentSelection(prevState => prevState + 1);
                  });
                }}
              >
                <Pick key={currentSelection}>
                  <CardImg
                    src={selectionData[1].pictureUrl}
                    style={{
                      viewTransitionName: `card-${selectionData[1].slug}`,
                    }}
                    width={320}
                  />
                </Pick>
                {currentSelection === 0 && <Pulsation />}
              </PickButton>
            </CenterContent>
          </>
        )}
        <Team
          viewTransition
          label={formatMessage(messages.yourTeam)}
          team={state.yourTeam}
          displayResult={displayResult}
          currentSelection={currentSelection}
        />
      </Content>
      <StadiumWrapper>
        <Stadium
          fieldUrl={fieldUrl}
          completion={Math.max(0.2 * state.yourTeam.length, 0.19)}
        />
      </StadiumWrapper>
    </Root>
  );
};

const PvEPageComponent = ({
  conference,
}: {
  conference: keyof typeof CONFERENCES_DATA_MAP;
}) => {
  const cardSlugs = CONFERENCES_DATA_MAP[conference].data;

  const { data } = useSuspenseQuery(PVE_NBA_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      slugs: cardSlugs,
    },
  });

  const playersData = useMemo(
    () =>
      buildData(
        data.anyCards
          .filter(card => isType(card, 'NBACard'))
          .filter(e => e.score && e.score > 0)
      ),
    // regenerage random card slugs on retry
    // league is also required to trigger data regen on unknown league redirection (there might be a better way to do this)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conference]
  );

  const conferenceTitle =
    CONFERENCES_DATA_MAP[conference as 'western' | 'eastern'].title;

  return (
    <PvEPageImplementation
      key={conferenceTitle}
      playersData={playersData}
      fieldUrl={field}
      conferenceTitle={conferenceTitle}
    />
  );
};

const PvEPage = () => {
  const { conference } = useTypedParams<PageParams>();

  if (!Object.keys(CONFERENCES_DATA_MAP).includes(conference as string)) {
    return <Navigate replace to="/nba/minigames/pve/western" />;
  }

  return (
    <Suspense fallback={<LoadingIndicator fullScreen />}>
      <PvEPageComponent
        conference={conference as keyof typeof CONFERENCES_DATA_MAP}
      />
    </Suspense>
  );
};

export default PvEPage;
